export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const FALLBACK_FIRST_DAY_BY_LANGUAGE: Record<string, WeekdayIndex> = {
  en: 0,
  de: 1,
  it: 1,
  fr: 1,
  es: 1,
};

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function parseDateInput(value: string) {
  const [year = 0, month = 1, day = 1] = value.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

export function capitalize(value: string, locale: string) {
  return value ? value.charAt(0).toLocaleUpperCase(locale) + value.slice(1) : value;
}

export function toDateInput(date: Date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function firstOfMonth(value: string) {
  const [year = 0, month = 1] = value.split("-").map(Number);
  return `${year}-${pad(month)}-01`;
}

export function addDays(value: string, amount: number) {
  const date = parseDateInput(value);
  date.setUTCDate(date.getUTCDate() + amount);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function addMonths(value: string, amount: number) {
  const date = parseDateInput(firstOfMonth(value));
  date.setUTCMonth(date.getUTCMonth() + amount);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-01`;
}

export function compareDateInput(left: string, right: string) {
  return left.localeCompare(right);
}

export function formatMonthYear(value: string, locale: string) {
  return capitalize(
    new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(parseDateInput(`${value}-01`)),
    locale,
  );
}

export function getFirstDayOfWeekForLocale(locale: string): WeekdayIndex {
  if (typeof Intl.Locale !== "undefined") {
    const localeInfo = new Intl.Locale(locale) as Intl.Locale & {
      weekInfo?: {
        firstDay?: number;
      };
    };
    const weekInfo = localeInfo.weekInfo;
    if (weekInfo?.firstDay) {
      return weekInfo.firstDay === 7 ? 0 : (weekInfo.firstDay as WeekdayIndex);
    }
  }

  const [language = "en"] = locale.toLowerCase().split("-");
  return FALLBACK_FIRST_DAY_BY_LANGUAGE[language] ?? 1;
}

export function getMonthNames(locale: string, format: "long" | "short" = "long") {
  return Array.from({ length: 12 }, (_, index) =>
    capitalize(new Intl.DateTimeFormat(locale, { month: format }).format(new Date(Date.UTC(2024, index, 1))), locale),
  );
}

export function getWeekdayLabels(locale: string, firstDayOfWeek: WeekdayIndex) {
  return Array.from({ length: 7 }, (_, index) => {
    const dayOffset = (firstDayOfWeek + index) % 7;
    const date = new Date(Date.UTC(2024, 0, 7 + dayOffset));

    return capitalize(new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date), locale).replace(".", "");
  });
}
